import React from "react";
import { Box, Typography } from "@mui/material";
import { TimelineTick, TimelineZoom } from "./timeline-range";

const TODAY_COLUMN_BACKGROUND = "var(--mela-color-primary-soft-bg)";
const TODAY_TEXT_COLOR = "var(--mela-color-primary-soft-text)";
const GROUP_ROW_HEIGHT = 20;
const TICK_ROW_HEIGHT = 38;
export const TIMELINE_HEADER_HEIGHT = GROUP_ROW_HEIGHT + TICK_ROW_HEIGHT;

interface TimelineHeaderProps {
  ticks: TimelineTick[];
  zoom: TimelineZoom;
  width: number;
  viewportStart?: number;
  viewportWidth?: number;
  viewportVisibleStart?: number;
  viewportVisibleWidth?: number;
  stickyLeftOffset?: number;
  locale?: string;
}

interface TimelineHeaderGroup {
  key: string;
  label: string;
  width: number;
  isToday: boolean;
}

type TimelineVisibleTick = TimelineTick & {
  key: string;
};

interface TimelineStickyGroupLabel {
  label: string;
  isToday: boolean;
}

const getGroupKey = (date: Date, zoom: TimelineZoom): string => {
  if (zoom === "hour" || zoom === "half-day") {
    return [date.getFullYear(), date.getMonth(), date.getDate()].join("-");
  }

  if (
    zoom === "month" ||
    zoom === "quarter" ||
    zoom === "semester" ||
    zoom === "year"
  ) {
    return String(date.getFullYear());
  }

  return `${date.getFullYear()}-${date.getMonth()}`;
};

const normalizeTimelineLocale = (locale?: string): string =>
  locale?.replace("_", "-") || "it-IT";

const getGroupLabel = (
  date: Date,
  zoom: TimelineZoom,
  locale?: string,
): string => {
  const normalizedLocale = normalizeTimelineLocale(locale);

  if (zoom === "hour" || zoom === "half-day") {
    return date.toLocaleDateString(normalizedLocale, {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
    });
  }

  if (
    zoom === "month" ||
    zoom === "quarter" ||
    zoom === "semester" ||
    zoom === "year"
  ) {
    return date.toLocaleDateString(normalizedLocale, { year: "numeric" });
  }

  return date.toLocaleDateString(normalizedLocale, {
    month: "long",
    year: "numeric",
  });
};

const buildHeaderGroups = (
  ticks: TimelineVisibleTick[],
  zoom: TimelineZoom,
  locale?: string,
): TimelineHeaderGroup[] =>
  ticks.reduce<TimelineHeaderGroup[]>((groups, tick) => {
    const key = getGroupKey(tick.start, zoom);
    const currentGroup = groups[groups.length - 1];

    if (currentGroup?.key === key) {
      currentGroup.width += tick.width;
      currentGroup.isToday = currentGroup.isToday || tick.isToday;
      return groups;
    }

    groups.push({
      key,
      label: getGroupLabel(tick.start, zoom, locale),
      width: tick.width,
      isToday: tick.isToday,
    });

    return groups;
  }, []);

const getVisibleTicks = (
  ticks: TimelineTick[],
  viewportStart: number,
  viewportWidth: number,
): TimelineVisibleTick[] => {
  if (viewportWidth <= 0) {
    return [];
  }

  const viewportEnd = viewportStart + viewportWidth;
  let tickOffset = 0;
  const visibleTicks: TimelineVisibleTick[] = [];

  ticks.forEach((tick) => {
    const tickStart = tickOffset;
    const tickEnd = tickStart + tick.width;
    tickOffset = tickEnd;

    if (tickEnd <= viewportStart || tickStart >= viewportEnd) {
      return;
    }

    const visibleStart = Math.max(tickStart, viewportStart);
    const visibleEnd = Math.min(tickEnd, viewportEnd);
    const visibleWidth = visibleEnd - visibleStart;

    if (visibleWidth <= 0) {
      return;
    }

    visibleTicks.push({
      ...tick,
      key: `${tick.key}:${visibleStart}:${visibleEnd}`,
      width: visibleWidth,
    });
  });

  return visibleTicks;
};

const getStickyMonthYearLabel = (
  ticks: TimelineTick[],
  viewportStart: number,
  viewportWidth: number,
  locale?: string,
): TimelineStickyGroupLabel | null => {
  if (viewportWidth <= 0) {
    return null;
  }

  const viewportEnd = viewportStart + viewportWidth;
  let tickOffset = 0;

  for (const tick of ticks) {
    const tickStart = tickOffset;
    const tickEnd = tickStart + tick.width;
    tickOffset = tickEnd;

    if (tickEnd <= viewportStart) {
      continue;
    }

    if (tickStart >= viewportEnd) {
      return null;
    }

    return {
      label: getGroupLabel(tick.start, "month", locale),
      isToday: tick.isToday,
    };
  }

  return null;
};

export const TimelineHeader = React.memo(function TimelineHeader({
  ticks,
  zoom,
  width,
  viewportStart = 0,
  viewportWidth = width,
  viewportVisibleStart = viewportStart,
  viewportVisibleWidth = viewportWidth,
  stickyLeftOffset = 0,
  locale,
}: TimelineHeaderProps) {
  const safeViewportStart = Math.max(0, Math.min(width, viewportStart));
  const safeViewportWidth = Math.max(
    0,
    Math.min(width - safeViewportStart, viewportWidth),
  );
  const safeViewportVisibleStart = Math.max(
    0,
    Math.min(width, viewportVisibleStart),
  );
  const safeViewportVisibleWidth = Math.max(
    0,
    Math.min(width - safeViewportVisibleStart, viewportVisibleWidth),
  );
  const visibleTicks = React.useMemo(
    () => getVisibleTicks(ticks, safeViewportStart, safeViewportWidth),
    [safeViewportStart, safeViewportWidth, ticks],
  );
  const groups = React.useMemo(
    () => buildHeaderGroups(visibleTicks, zoom, locale),
    [locale, visibleTicks, zoom],
  );
  const stickyMonthYearLabel = React.useMemo(
    () =>
      zoom === "month"
        ? getStickyMonthYearLabel(
            ticks,
            safeViewportVisibleStart,
            safeViewportVisibleWidth,
            locale,
          )
        : null,
    [locale, safeViewportVisibleStart, safeViewportVisibleWidth, ticks, zoom],
  );
  const showSubLabel = zoom !== "month";
  const showGroupRow = zoom !== "year";
  const tickRowHeight = showGroupRow ? TICK_ROW_HEIGHT : TIMELINE_HEADER_HEIGHT;
  const isPeriodZoom =
    zoom === "month" ||
    zoom === "quarter" ||
    zoom === "semester" ||
    zoom === "year";

  return (
    <Box
      sx={{
        position: "relative",
        width,
        minWidth: width,
        height: TIMELINE_HEADER_HEIGHT,
        background: "#ffffff",
      }}
    >
      {stickyMonthYearLabel && (
        <Typography
          component="div"
          sx={{
            position: "absolute",
            top: 0,
            left: safeViewportVisibleStart + stickyLeftOffset + 8,
            zIndex: 3,
            height: GROUP_ROW_HEIGHT,
            maxWidth: Math.max(48, safeViewportVisibleWidth - 16),
            color: stickyMonthYearLabel.isToday ? TODAY_TEXT_COLOR : "#475467",
            display: "flex",
            alignItems: "center",
            fontSize: 11,
            fontWeight: 700,
            lineHeight: "16px",
            backgroundColor: "#ffffff",
            overflow: "hidden",
            px: 0.25,
            pointerEvents: "none",
            textOverflow: "ellipsis",
            textTransform: "capitalize",
            whiteSpace: "nowrap",
          }}
        >
          {stickyMonthYearLabel.label}
        </Typography>
      )}
      <Box
        sx={{
          position: "absolute",
          left: safeViewportStart,
          width: safeViewportWidth,
          minWidth: safeViewportWidth,
          height: TIMELINE_HEADER_HEIGHT,
        }}
      >
        {showGroupRow ? (
          <Box
            sx={{
              display: "flex",
              height: GROUP_ROW_HEIGHT,
              borderBottom: "1px solid #e5e9f0",
            }}
          >
            {groups.map((group) => (
              <Box
                key={group.key}
                sx={{
                  width: group.width,
                  minWidth: group.width,
                  borderRight: "1px solid #d9dee6",
                  px: 0.75,
                  overflow: "visible",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {zoom !== "month" && (
                  <Typography
                    component="div"
                    sx={{
                      color: group.isToday ? TODAY_TEXT_COLOR : "#475467",
                      position: "sticky",
                      left: stickyLeftOffset + 8,
                      zIndex: 1,
                      display: "block",
                      maxWidth: Math.max(48, group.width - 12),
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: "16px",
                      backgroundColor: "#ffffff",
                      overflow: "hidden",
                      px: 0.25,
                      textOverflow: "ellipsis",
                      textTransform: "capitalize",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {group.label}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : null}

        <Box sx={{ display: "flex", height: tickRowHeight }}>
          {visibleTicks.map((tick) => (
            <Box
              key={tick.key}
              sx={{
                width: tick.width,
                minWidth: tick.width,
                borderRight: "1px solid #d9dee6",
                backgroundColor:
                  zoom === "day" && tick.isToday
                    ? TODAY_COLUMN_BACKGROUND
                    : tick.isWeekend
                      ? "#f7f9fb"
                      : "#ffffff",
                px: 0.5,
                py: 0.25,
                overflow: "hidden",
              }}
            >
              <Typography
                component="div"
                sx={{
                  fontSize: 12,
                  lineHeight: "16px",
                  fontWeight: 600,
                  color: tick.isToday ? TODAY_TEXT_COLOR : "#1f2937",
                  textTransform: isPeriodZoom ? "capitalize" : undefined,
                  whiteSpace: "nowrap",
                }}
              >
                {tick.label}
              </Typography>
              {showSubLabel && (
                <Typography
                  component="div"
                  sx={{
                    fontSize: 10,
                    lineHeight: "14px",
                    color: "#6b7280",
                    textTransform: "capitalize",
                    whiteSpace: "nowrap",
                  }}
                >
                  {tick.subLabel}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
});
